import "../JSONTools/JSONformatter.css";
import ToolHeader from "../../Components/ToolHeader";
import { ToolModeToggle } from "../../Components/ToolActionButtons";
import "./JWTEncode.css";
import { Row, Col } from "react-bootstrap";
import React from "react";
import { useState, useEffect, useCallback } from "react";

import FormatterPanel from "../../HomePage/FormatterPanel";
import { InputHeaderButtons, OutputHeaderButtons } from "../JSONTools/JSONToolsHeaderButtons";
import JWTsecretRow from "../../HomePage/JWTsecretRow";
import { ReactComponent as CaretDownIcon } from "../../Icons/CaretDown.svg";
import { JWTDecode, JWTEncode as JWTEncodeAPI } from "../../Services/APIService";
import { SampleJWT } from "../../Services/Tools";

const sampleSecret = "a-string-secret-at-least-256-bits-long";
const defaultSampleToken = SampleJWT[0]?.value || "";

const normalizeTokenText = (value) => value.replace(/\s/g, "");

const defaultHeader = {
  alg: "HS256",
  typ: "JWT"
};

const defaultPayload = {
  sub: "1234567890",
  name: "John Doe",
  admin: false,
  iat: 1516239022
};

function JWTEncode() {
  const [mode, setMode] = useState("encode");
  const [token, setToken] = useState(defaultSampleToken);
  const [secret, setSecret] = useState(sampleSecret);
  const [decodedHeader, setDecodedHeader] = useState(defaultHeader);
  const [decodedPayload, setDecodedPayload] = useState(defaultPayload);
  const [selectedAlgo, setSelectedAlgo] = useState("HS256");
  const [showDropdown, setShowDropdown] = useState(false);
  const [useBase64Url, setUseBase64Url] = useState(true);
  const [hasHeaderCopied, setHasHeaderCopied] = useState(false)
  const [hasPayloadCopied, setHasPayloadCopied] = useState(false)
  const [hasSecretCopied, setHasSecretCopied] = useState(false)


  const jwtAlgorithms = SampleJWT.map(({ algorithm }) => algorithm);
  const isDecodeMode = mode === "decode";
  const isTokenPanelEditable = isDecodeMode;
  const areClaimsEditable = !isDecodeMode;
  const toolFav = { toolName: 'JWT Decoder/Encoder', Iconpath: "/FileCodeRaw.svg" };
  const colors = ["#E37134", "#9473F7", "#8FC98A"];
  const [sentences, setSentences] = useState(defaultSampleToken.split("."));


  const parseEditableJson = (value) => {
    try {
      return {
        isValid: true,
        value: JSON.parse(value)
      };
    } catch {
      return {
        isValid: false,
        value
      };
    }
  };

  const decodeJWT = async (tokenValue) => {
    if (!tokenValue) {
      setSentences([]);
      setDecodedHeader("");
      setDecodedPayload("");
      return;
    }

    try {
      const cleanToken = normalizeTokenText(tokenValue);

      setToken(cleanToken);
      setSentences(cleanToken.split("."));
      const response = await JWTDecode({ token: cleanToken });
      setDecodedHeader(response.data.header);
      setDecodedPayload(response.data.payload);

    } catch (error) {
      console.log("Invalid token:", error);
      setDecodedHeader("");
      setDecodedPayload("");
    }
  };

  const encodeJWT = useCallback(async (claims, header) => {
    try {
      const payload = {
        algorithm: selectedAlgo,
        header,
        claims,
        secret,
        expiresInSeconds: 3600
      };

      const response = await JWTEncodeAPI(payload);

      const encodedToken =
        response.data.token ||
        response.data.jwt ||
        response.data.encoded;

      if (encodedToken) {
        setToken(encodedToken);
        setSentences(encodedToken.split("."));
      }
    } catch (error) {
      console.log(error);
    }
  }, [secret, selectedAlgo]);

  useEffect(() => {
    if (
      mode === "encode" &&
      typeof decodedHeader === "object" &&
      typeof decodedPayload === "object"
    ) {
      encodeJWT(decodedPayload, decodedHeader);
    }
  }, [decodedHeader, decodedPayload, encodeJWT, mode]);

  const handleAlgorithmChange = (algorithm) => {
    const sample = SampleJWT.find((item) => item.algorithm === algorithm);

    setSelectedAlgo(algorithm);
    setShowDropdown(false);

    if (sample?.value) {
      setToken(sample.value);
      decodeJWT(sample.value);
    }
  };

  const handleToggle = () => {
    const newValue = !useBase64Url;
    setUseBase64Url(newValue);
  };

  const handleModeChange = (nextMode) => {
    setMode(nextMode);

    if (nextMode === "decode" && token) {
      decodeJWT(token);
    }
  };

  const tokenPanel = () => (
    <Col md={6} className="jwt-left-panel" key="tokenPanel">
      <div className="jwt-section-header">
        <span>{isDecodeMode ? "Encoded Token" : "Encode Token"}</span>
        <div className="IndentDropdownWrapper">
          Generate Example
          <button
            type="button"
            className="IndentDropdownToggle"
            onClick={() => setShowDropdown((prev) => !prev)}
          >
            <span className="Algo-DropdownText">{selectedAlgo || "Select Format"}
              <CaretDownIcon />
            </span>
          </button>
          {showDropdown && (
            <div className="IndentDropdownMenu">
              {jwtAlgorithms.map((value) => (
                <button
                  key={value}
                  type="button"
                  className="IndentDropdownItem"
                  onClick={() => handleAlgorithmChange(value)}
                >
                  {value}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <FormatterPanel
        title="JSON Web Token (JWT)"
        panelStyle={{ width: "100%", height: "100%" }}
        customContent={
          <div
            contentEditable={isTokenPanelEditable}
            suppressContentEditableWarning
            className="jwt-token-textarea"
            onInput={(e) => {
              const textContent = normalizeTokenText(
                e.currentTarget.textContent
              );
              setToken(textContent);
              if (mode === "decode") {
                decodeJWT(textContent);
              }
            }}
            onPaste={(e) => {
              if (!isTokenPanelEditable) {
                return;
              }
              e.preventDefault();
              const pastedText = normalizeTokenText(e.clipboardData.getData("text"));
              setToken(pastedText);
              decodeJWT(pastedText);
            }}
          >
            {sentences.map((sentence, index) => (
              <React.Fragment key={`${sentence}-${index}`}>
                <span
                  style={{
                    color: colors[index % colors.length],
                    whiteSpace: "wrap",
                    marginRight: "5px"
                  }}
                >
                  {sentence.trim()}
                </span>
                {index < sentences.length - 1 && <span>.</span>}
              </React.Fragment>
            ))}
          </div>}
        headerButtons={
          <>
            <InputHeaderButtons
              onClear={clearToken}
            />
            <OutputHeaderButtons outputText={token} fileName="jwt.txt" mimeType="text/plain;charset=utf-8" />
          </>
        }
      />
    </Col>
  );

  const outputPanel = () => (
    <Col md={6} className="jwt-right-panel" key="outputPanel">
      <div className="jwt-section-header">
        <span>Decoded Header</span>
      </div>
      <JWTsecretRow
        title="JSON"
        value={decodedHeader}
        onCopy={() => copyToClipboard(decodedHeader, "header")}
        hasCopied={hasHeaderCopied}
        onClear={() => setDecodedHeader("")}
        editable={areClaimsEditable}
        onChange={(value) => {
          const parsed = parseEditableJson(value);
          setDecodedHeader(parsed.value);
        }}
      />
      <div className="jwt-section-header">
        <span>Decoded Payload</span>
      </div>
      <JWTsecretRow
        title="Decoded Payload"
        value={decodedPayload}
        onCopy={() => copyToClipboard(decodedPayload, "payload")}
        hasCopied={hasPayloadCopied}
        onClear={() => setDecodedPayload("")}
        editable={areClaimsEditable}
        onChange={(value) => {
          const parsed = parseEditableJson(value);
          setDecodedPayload(parsed.value);
        }}
      />
      <div className="jwt-secret-header-row">
        <div>
          <div className="jwt-secret-header">JWT Signature Verification</div>
          <div className="jwt-secret-optional">Enter the secret used to sign the JWT below</div>
        </div>
        <div className="jwt-secret-toggle-row">
          <div className="base64url-toggle-container">
            <span className="base64url-toggle-label">
              BASE64URL ENCODED
            </span>
            <button
              type="button"
              className={`base64url-toggle ${useBase64Url ? "active" : ""}`}
              onClick={handleToggle}>
              <span className="toggle-knob" />
            </button>
          </div>
        </div>
      </div>
      <JWTsecretRow
        title="Secret"
        value={secret}
        onCopy={() => copyToClipboard(secret, "secret")}
        hasCopied={hasSecretCopied}
        onClear={() => setSecret("")}
        editable
        onChange={setSecret}
      />
    </Col>
  );
  const copyToClipboard = async (value, type) => {
    const clipboardValue =
      typeof value === "string" ? value : JSON.stringify(value, null, 2);

    try {
      await navigator.clipboard.writeText(clipboardValue);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = clipboardValue;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      textarea.remove();
    }

    if (type === "header") {
      setHasHeaderCopied(true)
      setTimeout(() => {
        setHasHeaderCopied(false)
      }, 2000);
    }
    else if (type === "payload") {
      setHasPayloadCopied(true)
      setTimeout(() => {
        setHasPayloadCopied(false)
      }, 2000);
    }
    else {
      setHasSecretCopied(true)
      setTimeout(() => {
        setHasSecretCopied(false)
      }, 2000);
    }
  };

  const clearToken = () => {
    setToken("");
    setDecodedHeader("");
    setDecodedPayload("");
  };


  return (
    <>
      <Col>
        <ToolHeader title="JWT Decoder/Encoder" description="Encode, decode, and verify JSON Web Tokens with HMAC-based signature support." toolFav={toolFav} />

        <ToolModeToggle
          options={[
            { label: "Decoder", value: "decode", className: "encode" },
            { label: "Encoder", value: "encode", className: "decode" }
          ]}
          activeValue={mode}
          onChange={handleModeChange}
        />

        <Row className="jwt-grid">
          {isDecodeMode ? [tokenPanel(), outputPanel()] : [outputPanel(), tokenPanel()]}
        </Row>
      </Col>
    </>
  );
}

export default JWTEncode;
